import { Card, CardColor, CardType, GameState, Player } from '../types'
import { DECK_COMPOSITION, STASH_DECK_COMPOSITION } from '../cardCatalog'
import { xmur3, mulberry32 } from './random'

export const shuffle = <T,>(array: T[], seed: string): T[] => {
  const newArray = [...array]
  const seedGen = xmur3(seed)
  const rand = mulberry32(seedGen())
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1))
      ;[newArray[i], newArray[j]] = [newArray[j], newArray[i]]
  }
  return newArray
}

const makeDeterministicId = (seed: string, ns: string, idx: number): string => {
  const gen = xmur3(`${seed}_${ns}_${idx}`)
  return gen().toString()
}

export const createColorDecks = (seed: string): Record<CardColor, Card[]> => {
  const decks: Record<CardColor, Card[]> = {
    [CardColor.Red]: [],
    [CardColor.Green]: [],
    [CardColor.Black]: [],
    [CardColor.Purple]: [],
    [CardColor.Yellow]: [],
    [CardColor.Pink]: [],
  };

  const colorCounts: Record<CardColor, number> = {
    [CardColor.Red]: 0,
    [CardColor.Green]: 0,
    [CardColor.Black]: 0,
    [CardColor.Purple]: 0,
    [CardColor.Yellow]: 0,
    [CardColor.Pink]: 0,
  }

  DECK_COMPOSITION.forEach(cardInfo => {
    const idx = colorCounts[cardInfo.color]
    const id = makeDeterministicId(seed, `color_${cardInfo.color}`, idx)
    decks[cardInfo.color].push({ ...cardInfo, id })
    colorCounts[cardInfo.color] = idx + 1
  });

  for (const color in decks) {
    decks[color as CardColor] = shuffle(decks[color as CardColor], seed + color);
  }

  return decks;
};

export const createStashDeck = (seed: string): Card[] => {
  const deck = STASH_DECK_COMPOSITION.map(cardInfo => {
    let color: CardColor;
    switch (cardInfo.type) {
      case CardType.Attack: color = CardColor.Red; break;
      case CardType.EnergySword: color = CardColor.Red; break;
      case CardType.FlyingScythe: color = CardColor.Red; break;
      case CardType.InfernoGauntlet: color = CardColor.Red; break;
      case CardType.Defense: color = CardColor.Yellow; break;
      case CardType.PsySearch: color = CardColor.Black; break;
      case CardType.Steal: color = CardColor.Purple; break;
      case CardType.StealTower: color = CardColor.Purple; break;
      case CardType.EyesOfMadness: color = CardColor.Purple; break;
      case CardType.MadnessControl: color = CardColor.Purple; break;
      case CardType.Larcendagger: color = CardColor.Purple; break;
      case CardType.TwilightShift: color = CardColor.Purple; break;
      case CardType.BodyUpgrade: color = CardColor.Pink; break;
      case CardType.FortunesFavor: color = CardColor.Yellow; break;
      case CardType.CursedIron: color = CardColor.Yellow; break;
      case CardType.ArcaneBoots: color = CardColor.Yellow; break;
      case CardType.Ale: color = CardColor.Yellow; break;
      case CardType.BrokenSecret: color = CardColor.Black; break;
      case CardType.Counterblast: color = CardColor.Pink; break;
      case CardType.PlantedBomb: color = CardColor.Red; break;
      case CardType.Frost: color = CardColor.Pink; break;
      case CardType.Invincible: color = CardColor.Pink; break;
      case CardType.Chromabow: color = CardColor.Red; break;
      case CardType.LifeCharger: color = CardColor.Green; break;
      case CardType.LifeDrain: color = CardColor.Red; break;
      case CardType.NaturalGrip: color = CardColor.Green; break;
      case CardType.Protector: color = CardColor.Yellow; break;
      case CardType.ThunderSprint: color = CardColor.Yellow; break;
      case CardType.Trembling: color = CardColor.Red; break;
      case CardType.SealOfSilence: color = CardColor.Yellow; break;
      case CardType.VoodooDoll: color = CardColor.Yellow; break;
      default: color = CardColor.Black; // fallback
    }
    return {
      ...cardInfo,
      id: '',
      color,
    };
  });
  const assigned = deck.map((c, i) => ({ ...c, id: makeDeterministicId(seed, 'stash', i) }))
  return shuffle(assigned, seed);
};

export const ensureDealingDeck = (state: GameState, requiredCount: number): GameState => {
  let currentDealingDeck = [...state.dealingDeck]
  let currentColorDecks = { ...state.colorDecks }
  
  let availableColors = Object.values(CardColor).filter(color => currentColorDecks[color].length > 0)
  
  while (currentDealingDeck.length < requiredCount && availableColors.length > 0) {
    const newCardsForDealingDeck: Card[] = []
    availableColors.forEach(color => {
      const deck = currentColorDecks[color]
      if (deck.length > 0) {
        const card = deck[deck.length - 1]
        newCardsForDealingDeck.push(card)
        currentColorDecks[color] = deck.slice(0, -1)
      }
    })
    currentDealingDeck = [
      ...currentDealingDeck,
      ...shuffle(newCardsForDealingDeck, state.seed + '_dealing_' + currentDealingDeck.length),
    ]
    availableColors = Object.values(CardColor).filter(color => currentColorDecks[color].length > 0)
  }
  
  return { ...state, dealingDeck: currentDealingDeck, colorDecks: currentColorDecks }
}

export const drawFromDealingDeck = (state: GameState, playerId: number, count: number): GameState => {
  const playerIndex = state.players.findIndex(p => p.id === playerId)
  if (playerIndex === -1 || state.dealingDeck.length === 0) return state

  const cardsToDrawCount = Math.min(count, state.dealingDeck.length)
  const drawnCards = state.dealingDeck.slice(-cardsToDrawCount).map(c => ({ ...c, ownerId: playerId }))
  const remainingDealingDeck = state.dealingDeck.slice(0, -cardsToDrawCount)

  const newPlayers = state.players.map((p, idx) => {
    if (idx !== playerIndex) return p
    return { ...p, hand: [...p.hand, ...drawnCards] }
  }) as [Player, Player]

  return { ...state, players: newPlayers, dealingDeck: remainingDealingDeck }
}
